import React, { useEffect } from 'react';
import { Box, Typography, Button, Chip, Avatar, Paper } from '@mui/material';
import { useWizard, initialWizardData } from '../../context/WizardContext';
import { useParams } from 'react-router-dom';
import { useAdvisor } from '../../hooks/useAdvisor';
import { formatAdvisorName } from '../../utils/formatAdvisorName';
import planScoutLogo from '../../assets/PlanScout_orange_Logo.jpg';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HealthSpanResult, { BOOK_ANCHOR_ID } from './HealthSpanResult';

interface ThankYouStepProps {
  setIsSubmitted: (value: boolean) => void;
  healthSpanResult?: any;
}

const ThankYouStep: React.FC<ThankYouStepProps> = ({ setIsSubmitted, healthSpanResult }) => {
  const { wizardData, updateWizardData, setCurrentStep } = useWizard();
  const { idCustomer } = useParams<{ idCustomer: string }>();
  const advisor = useAdvisor();
  const hasCustomerId = !!idCustomer;
  const isSeiaAdvisor = !!advisor; // SEIA advisor if found in advisors.ts
  
  // Derive wizard path from intakeVersion + automationEnabled (database-driven)
  // Also check URL ?mode=basic override so dual-mode advisors show the right content
  const urlModeOverride = new URLSearchParams(window.location.search).get('mode');
  const isDemoAdvisor = advisor?.intakeVersion === 'demo';
  const isFullAutomation = !urlModeOverride && advisor?.intakeVersion === 'full' && advisor?.automationEnabled === true;
  const isBasicAutomation = urlModeOverride === 'basic' || urlModeOverride === 'basic-healthspan' || advisor?.intakeVersion === 'basic' || advisor?.intakeVersion === 'basic-healthspan';

  const calendlyUrl = advisor?.features?.branding?.calendlyUrl;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Load Calendly widget script
  useEffect(() => {
    if (!calendlyUrl) return;
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, [calendlyUrl]);

  const handleStartOver = () => {
    // Clear session from sessionStorage
    sessionStorage.removeItem('wizardSessionId');
    
    // First reset to step 0 to prevent the progress hook from saving
    setCurrentStep(0);
    
    // Then reset the wizard data to initial state, including clearing the sessionId
    // Use setTimeout to ensure step change happens first
    setTimeout(() => {
      // Reset to initial state
      updateWizardData(initialWizardData);
      // Unset submitted state
      setIsSubmitted(false);
    }, 0);
  };

  const renderContent = () => {
    if (!hasCustomerId) {
      // Demo mode - keep as is
      return (
        <>
          <Typography variant="h6" paragraph sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
            ✅ Your Financial Plan is in the Works!
          </Typography>
          <Typography variant="body1" paragraph>
            Thanks for completing your PlanScout interview <Typography component="span" fontWeight="bold">— we've got it from here.</Typography>
          </Typography>
          <Typography variant="body1" paragraph>
            Our team is now crafting a personalized financial plan for you, and it will be ready <Typography component="span" fontWeight="bold">within five business days.</Typography>
          </Typography>
          <Box sx={{ 
            width: '100%', 
            borderBottom: '1px solid #e0e0e0',
            my: 3 
          }} />
          <Typography variant="h6" paragraph fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
            What Happens Next:
          </Typography>
          <Typography variant="body1" paragraph>
            We'll email you as soon as your plan is ready, along with simple instructions to access it through our platform.
          </Typography>
          <Box sx={{ 
            width: '100%', 
            borderBottom: '1px solid #e0e0e0',
            my: 3 
          }} />
          <Typography variant="h6" paragraph fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
            Thanks for trying PlanScout!
          </Typography>

          <Box sx={{ pl: 3 }}>
            <Typography variant="body1" paragraph>
              <Typography component="span" fontWeight="bold">✔ Custom branded, flexible plans:</Typography> Get professional and custom-branded plans perfect for attracting new clients and deepening relationships with current ones.
            </Typography>
            <Typography variant="body1" paragraph>
              <Typography component="span" fontWeight="bold">✔ Find overlooked tax strategies:</Typography> Unlock 50% more after-tax income and triple inheritances compared to other tools.
            </Typography>
            <Typography variant="body1" paragraph>
              <Typography component="span" fontWeight="bold">✔ Cut planning time by over 80%</Typography> – Spend less time crunching numbers and more time building client relationships.
            </Typography>
            <Typography variant="body1" paragraph>
              <Typography component="span" fontWeight="bold">✔ Save over $1,000 per plan</Typography> – we've crunched the numbers so that you don't have to in our <a href="https://planscout.com/outsourcing-guide-download/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Outsourcing Guide</a>.
            </Typography>
                     </Box>
         </>
       );
    }

    if (isDemoAdvisor) {
      // Demo advisor mode - show special content
      return (
        <>
          <Typography variant="h6" paragraph sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            We've got it from here! An email in your inbox with a plan presentation is up next.
          </Typography>
          
          <Typography variant="body1" paragraph>
            Wow. That was easy, right?
          </Typography>
          
          <Typography variant="body1" paragraph>
            Want some help getting started reading your plan? Check out our <a href="https://planscout.com/how-to-read-a-plan/" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>guide for your demo plan</a>.
          </Typography>
          
          <Typography variant="body1" paragraph>
            Want to do another demo plan?
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartOver}
              size="large"
              sx={{ px: 4, py: 1.5 }}
            >
              Start Over
            </Button>
          </Box>
          
          <Typography variant="body1" paragraph sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
            Once you are onboarded, the confirmation page will look like this sample below:
          </Typography>
          
          {/* Sample confirmation page in a box */}
          <Paper 
            elevation={3}
            sx={{ 
              p: 3, 
              backgroundColor: '#f5f5f5',
              border: '2px dashed #ccc',
              borderRadius: 2
            }}
          >
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, color: '#666' }}>
              [Sample Confirmation Page]
            </Typography>
            
            <Typography variant="h6" paragraph sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
              ✅ Your Financial Plan is in the Works!
            </Typography>
            <Typography variant="body1" paragraph>
              Thanks for completing your financial assessment interview <Typography component="span" fontWeight="bold">— we've got it from here.</Typography>
            </Typography>
            <Typography variant="body1" paragraph>
              Our team is now crafting a personalized financial plan for you, and it will be ready soon.
            </Typography>

            {/* Advisor Information Block */}
            {advisor && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'flex-start' },
                gap: 3,
                mt: 4,
                p: 3,
                backgroundColor: '#fff',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar
                  src={advisor.advisorPhotoUrl}
                  alt={advisor.name}
                  sx={{ 
                    width: { xs: 80, sm: 100 }, 
                    height: { xs: 80, sm: 100 },
                    border: '3px solid #fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                />
                <Box sx={{ 
                  textAlign: { xs: 'center', sm: 'left' },
                  flex: 1
                }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {formatAdvisorName(advisor.name)}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 1,
                    alignItems: { xs: 'center', sm: 'flex-start' }
                  }}>
                    <Chip 
                      label={advisor.company}
                      size="small"
                      sx={{ 
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 'medium'
                      }}
                    />
                    <Chip 
                      icon={<EmailIcon />}
                      label={advisor.email}
                      size="small"
                      variant="outlined"
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip 
                      icon={<PhoneIcon />}
                      label={advisor.phone}
                      size="small"
                      variant="outlined"
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </>
      );
    } else if (isFullAutomation && hasCustomerId && advisor) {
      // Full automation mode — separate branch for future customization
      return (
        <>
          <Typography variant="h6" paragraph sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
            ✅ Your Financial Plan is in the Works!
          </Typography>
          <Typography variant="body1" paragraph>
            Thanks for completing your financial assessment interview <Typography component="span" fontWeight="bold">— we've got it from here.</Typography>
          </Typography>
          <Typography variant="body1" paragraph>
            Our team is now crafting a personalized financial plan for you, and it will be ready soon.
          </Typography>

          {/* Advisor Information Block */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' },
            gap: 3,
            mt: 4,
            p: 3,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '1px solid #e9ecef'
          }}>
            <Avatar
              src={advisor.advisorPhotoUrl}
              alt={advisor.name}
              sx={{
                width: { xs: 100, sm: 120 },
                height: { xs: 100, sm: 120 },
                border: '3px solid #fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            />
            <Box sx={{
              textAlign: { xs: 'center', sm: 'left' },
              flex: 1
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {formatAdvisorName(advisor.name)}
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: { xs: 'center', sm: 'flex-start' }
              }}>
                <Chip
                  label={advisor.company}
                  size="small"
                  sx={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 'medium'
                  }}
                />
                <Chip
                  icon={<EmailIcon />}
                  label={advisor.email}
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => window.open(`mailto:${advisor.email}`)}
                />
                <Chip
                  icon={<PhoneIcon />}
                  label={advisor.phone}
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => window.open(`tel:${advisor.phone}`)}
                />
              </Box>
            </Box>
          </Box>
        </>
      );
    } else if (isBasicAutomation && hasCustomerId && advisor) {
      // Basic automation mode — seminar intake diagnostic
      return (
        <>
          {/* Limited Interview – HealthSpan: the snapshot IS the header (personalized
              thanks + themes + questions). Otherwise show the standard plan message. */}
          {healthSpanResult ? (
            <Box sx={{ width: '100%', mb: 2 }}>
              <HealthSpanResult result={healthSpanResult} advisor={advisor} personal={wizardData.personal} />
            </Box>
          ) : (
            <>
              {/* Heading */}
              <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.3rem', sm: '1.5rem' }, mb: 1 }}>
                Thanks! Your Wealth Report is being processed
              </Typography>

              {/* Subtitle */}
              <Typography variant="body1" color="text.secondary" paragraph>
                Your advisor will reach out to schedule a brief conversation to review your personalized insights.
                {calendlyUrl ? ' Book a time below to get ahead of it.' : ''}
              </Typography>
            </>
          )}

          {/* Book the conversation (advisor card). Anchor target for the snapshot's
              "Book the conversation now" link. */}
          <Box id={BOOK_ANCHOR_ID} sx={{ width: '100%', mt: healthSpanResult ? 5 : 2 }}>
            {healthSpanResult ? (
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5 }}>
                Book the conversation
              </Typography>
            ) : (
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, fontSize: '0.7rem', ml: 0.5, mb: 0.5, display: 'block' }}>
                YOUR ADVISOR
              </Typography>
            )}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 3,
                border: '1px solid rgba(0, 0, 0, 0.06)',
                width: '100%'
              }}
            >
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                gap: 2.5
              }}>
                <Avatar
                  src={advisor.advisorPhotoUrl}
                  alt={advisor.name}
                  sx={{
                    width: { xs: 130, sm: 156 },
                    height: { xs: 130, sm: 156 },
                    border: '3px solid #fff',
                    boxShadow: 2
                  }}
                />
                <Box sx={{
                  flex: 1,
                  textAlign: { xs: 'center', sm: 'left' },
                  minWidth: 0
                }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {formatAdvisorName(advisor.name)}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                    {advisor.company}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#2BA784', fontWeight: 500, mb: 1.5 }}>
                    Available if you have questions
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}>
                    <Chip
                      icon={<EmailIcon />}
                      label={advisor.email}
                      variant="outlined"
                      size="small"
                      sx={{ '& .MuiChip-label': { fontSize: '0.75rem', px: 1 } }}
                    />
                    {advisor.phone && (
                      <Chip
                        icon={<PhoneIcon />}
                        label={advisor.phone}
                        variant="outlined"
                        size="small"
                        sx={{ '& .MuiChip-label': { fontSize: '0.75rem', px: 1 } }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Calendly embed */}
          {calendlyUrl && (
            <Box sx={{ width: '100%', mt: 3 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, fontSize: '0.7rem', ml: 0.5, mb: 0.5, display: 'block' }}>
                BOOK A TIME TO REVIEW YOUR REPORT
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  overflow: 'hidden'
                }}
              >
                <div
                  className="calendly-inline-widget"
                  data-url={calendlyUrl}
                  style={{ minWidth: '320px', height: '700px' }}
                />
              </Paper>
            </Box>
          )}

          {/* Upgrade CTA — only show when basic came from ?mode=basic override (advisor supports full flow) */}
          {urlModeOverride === 'basic' && (
            <Paper elevation={1} sx={{ p: 3, mt: 3, borderRadius: 2, bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Want a more comprehensive plan?
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Provide additional details about your employment, Social Security, savings, and goals for a deeper financial analysis.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const upgradeUrl = `${window.location.origin}/wizard/${idCustomer}?prefill=${wizardData.sessionId}`;
                  window.location.href = upgradeUrl;
                }}
              >
                Complete Full Assessment
              </Button>
            </Paper>
          )}

          {/* Disclaimer (HealthSpan snapshot only). "Powered by PlanScout" is the
              existing branded footer below — no need to duplicate it here. */}
          {healthSpanResult && (
            <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid rgba(0,0,0,0.08)', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {healthSpanResult.clientSnapshot?.disclaimer || 'This snapshot is a conversation starter, not a diagnosis, recommendation, or eligibility decision.'}
              </Typography>
            </Box>
          )}
        </>
      );
    } else if (hasCustomerId && advisor) {
      // Regular advisor mode with custom ID (non-demo)
      return (
        <>
          <Typography variant="h6" paragraph sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
            ✅ Your Financial Plan is in the Works!
          </Typography>
          <Typography variant="body1" paragraph>
            Thanks for completing your financial assessment interview <Typography component="span" fontWeight="bold">— we've got it from here.</Typography>
          </Typography>
          <Typography variant="body1" paragraph>
            Our team is now crafting a personalized financial plan for you, and it will be ready soon.
          </Typography>

          {/* Advisor Information Block */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' },
            gap: 3,
            mt: 4,
            p: 3,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '1px solid #e9ecef'
          }}>
            <Avatar
              src={advisor.advisorPhotoUrl}
              alt={advisor.name}
              sx={{ 
                width: { xs: 100, sm: 120 }, 
                height: { xs: 100, sm: 120 },
                border: '3px solid #fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            />
            <Box sx={{ 
              textAlign: { xs: 'center', sm: 'left' },
              flex: 1
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {formatAdvisorName(advisor.name)}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 1,
                alignItems: { xs: 'center', sm: 'flex-start' }
              }}>
                <Chip 
                  label={advisor.company}
                  size="small"
                  sx={{ 
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 'medium'
                  }}
                />
                <Chip 
                  icon={<EmailIcon />}
                  label={advisor.email}
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => window.open(`mailto:${advisor.email}`)}
                />
                <Chip 
                  icon={<PhoneIcon />}
                  label={advisor.phone}
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => window.open(`tel:${advisor.phone}`)}
                />
              </Box>
            </Box>
          </Box>
        </>
      );
    }

    // Fallback: advisor with customer ID but no advisor info found, or no customer ID
    return (
      <>
        <Typography variant="h6" paragraph sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
          ✅ Your Financial Plan is in the Works!
        </Typography>
        <Typography variant="body1" paragraph>
          Thanks for completing your PlanScout interview <Typography component="span" fontWeight="bold">— we've got it from here.</Typography>
        </Typography>
        <Typography variant="body1" paragraph>
          Our team is now crafting a personalized financial plan for you, and it will be ready soon.
        </Typography>
      </>
    );
  };

  return (
    <Box sx={{ 
      py: 6,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 3
    }}>
      <Box sx={{
        textAlign: 'left',
        width: '100%',
        maxWidth: 600
      }}>
        {renderContent()}
      </Box>

      {/* Powered by PlanScout footer */}
      {advisor?.features?.branding?.showPoweredByPlanScout !== false && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mt: 4,
          opacity: 0.6,
          '&:hover': {
            opacity: 0.8
          },
          transition: 'opacity 0.2s ease'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Powered by
          </Typography>
          <Box
            component="img"
            src={planScoutLogo}
            alt="PlanScout Logo"
            sx={{ height: 30, width: 'auto' }}
          />
        </Box>
      )}

      {!isDemoAdvisor && (
        <Button
          variant="outlined"
          color="primary"
          onClick={handleStartOver}
          sx={{ mt: 2 }}
        >
          Start Over
        </Button>
      )}
    </Box>
  );
};

export default ThankYouStep; 